/* CACHE-BUST 2026-07-28-pawslider-goldoutside-live */
/* ============================================================================
   MTL Silent Personalization Pipeline — Frontend
   v3 — 2026-04-20: AbortController timeout, Horizon swatch-button click hook,
                    URL-fallback variant detection, inFlight watchdog.
   ----------------------------------------------------------------------------
   Listens to the branded name input → debounces 500ms → calls Cloudflare
   Worker → writes properties[Personalization ID] + properties[_Personalization]
   to cart form → swaps preview image → gates ATC.

   Coexists with the existing mtl-personalization.js (layout/swatches/etc).
   Self-contained. Load after DOMContentLoaded.

   Worker: https://mtl-personalize.steep-lab-a49a.workers.dev
   ============================================================================ */
(function () {
  'use strict';

  const WORKER_URL = 'https://mtl-personalize.steep-lab-a49a.workers.dev/personalize';
  const DEBOUNCE_MS = 500;
  const FETCH_TIMEOUT_MS = 15000;       // hard cap on Worker call before abort + error
  const INFLIGHT_WATCHDOG_MS = 16500;   // safety net: force-clear inFlight if anything wedges

  // Shopify variant → everything we need to generate the correct sharedId and
  // render the correct UX for that variant. Add an entry per variant we roll
  // the silent pipeline out to.
  //   fulfillerVariant  : the fulfiller's internal variant id (used by the personalization API)
  //   templateProductId: the fulfiller's product id (whitelisted on the Worker)
  //   label            : swatch label the variant maps to on the PDP
  //   maxLength        : character limit enforced by the Worker for this product
  //   displayPrice     : what the ATC label shows (keeps the price in sync with Shopify)
  const VARIANT_MAP = {
    // Always With Me Heart Necklace
    47253644837013: { fulfillerVariant: 147718, templateProductId: '69dcf0ec94a2a2acde057407', label: 'Gold',   maxLength: 10, displayPrice: '$47.00' },
    47253644869781: { fulfillerVariant: 147721, templateProductId: '69dcf0ec94a2a2acde057407', label: 'Stainless Steel', maxLength: 10, displayPrice: '$47.00' },
    47354427736213: { fulfillerVariant: 147720, templateProductId: '69dcf0ec94a2a2acde057407', label: 'Rose Gold', maxLength: 10, displayPrice: '$47.00' },  // Rose Gold wired 2026-06-09 (real live price $42.00)
    // Paw Bar Name Necklace ("MAGGIE")
    47254673424533: { fulfillerVariant: 147706, templateProductId: '69dd0f9fffc76ae14901f12b', label: 'Gold',      maxLength: 12, displayPrice: '$49.00' },
    47254673457301: { fulfillerVariant: 147709, templateProductId: '69dd0f9fffc76ae14901f12b', label: 'Stainless Steel',    maxLength: 12, displayPrice: '$49.00' },
    47358222598293: { fulfillerVariant: 147708, templateProductId: '69dd0f9fffc76ae14901f12b', label: 'Rose Gold', maxLength: 12, displayPrice: '$49.00' },  // Rose Gold wired 2026-06-09 (real live price $39.00)
    // Pet Name Heart Necklace ("COOPER")
    47254656680085: { fulfillerVariant: 147718, templateProductId: '69dd0c660830a44cfa0e4826', label: 'Gold',      maxLength: 10, displayPrice: '$47.00' },
    47254656712853: { fulfillerVariant: 147721, templateProductId: '69dd0c660830a44cfa0e4826', label: 'Stainless Steel',    maxLength: 10, displayPrice: '$47.00' },
    47418850705557: { fulfillerVariant: 147720, templateProductId: '69dd0c660830a44cfa0e4826', label: 'Rose Gold', maxLength: 10, displayPrice: '$47.00' },  // added 2026-06-08
    // Sliding Bar Cat Paw — two-tone finishes (Gold Outside / Stainless Outside — gold-outside variant added 2026-07-26)
    47254716547221: { fulfillerVariant: 164458, templateProductId: '69dd16af0830a44cfa0e49a9', label: 'Stainless Outside', maxLength: 10, displayPrice: '$52.00' },
    47627032559765: { fulfillerVariant: 164457, templateProductId: '69dd16af0830a44cfa0e49a9', label: 'Gold Outside', maxLength: 10, displayPrice: '$52.00' },
    // Sliding Bar Dog Paw — two-tone finishes (Gold Outside / Stainless Outside — gold-outside variant added 2026-07-26)
    47252905230485: { fulfillerVariant: 164458, templateProductId: '69dcc04be8f2cd193a0ee0d5', label: 'Stainless Outside', maxLength: 10, displayPrice: '$52.00' },
    47627032133781: { fulfillerVariant: 164457, templateProductId: '69dcc04be8f2cd193a0ee0d5', label: 'Gold Outside', maxLength: 10, displayPrice: '$52.00' },
    // Engravable Cutout Heart Bar Necklace — Personalized Name (added 2026-05-30)
    47373182402709: { fulfillerVariant: 147733, templateProductId: '6a0ef23d3d36faaa3d048784', label: 'Gold',      maxLength: 12, displayPrice: '$39.00' },
    47373182435477: { fulfillerVariant: 147735, templateProductId: '6a0ef23d3d36faaa3d048784', label: 'Rose Gold', maxLength: 12, displayPrice: '$39.00' },
    47373182468245: { fulfillerVariant: 147736, templateProductId: '6a0ef23d3d36faaa3d048784', label: 'Stainless', maxLength: 12, displayPrice: '$39.00' },
    // Personalized Heartbeat Necklace (heartbeat-bar), added 2026-10-28
    68088071127189: { fulfillerVariant: 147733, templateProductId: '6ae130891a9dc4d693099a6d', label: 'Gold',      maxLength: 12, displayPrice: '$39.00' },
    68088071159957: { fulfillerVariant: 147735, templateProductId: '6ae130891a9dc4d693099a6d', label: 'Rose Gold', maxLength: 12, displayPrice: '$39.00' },
    68088071192725: { fulfillerVariant: 147736, templateProductId: '6ae130891a9dc4d693099a6d', label: 'Stainless', maxLength: 12, displayPrice: '$39.00' },
    // Engravable Cutout Cross Bar Necklace — Personalized Name (added 2026-05-30)
    47373943570581: { fulfillerVariant: 147741, templateProductId: '6a0f58153edf849312010796', label: 'Gold',      maxLength: 12, displayPrice: '$39.00' },
    47373943603349: { fulfillerVariant: 147743, templateProductId: '6a0f58153edf849312010796', label: 'Rose Gold', maxLength: 12, displayPrice: '$39.00' },
    47373943636117: { fulfillerVariant: 147744, templateProductId: '6a0f58153edf849312010796', label: 'Stainless', maxLength: 12, displayPrice: '$39.00' },
    // --- Restored 2026-06-09: these 3 SKUs were migrated to the unified
    //     mtl-personalization section (loads THIS main script) but their rows
    //     were never added here -> pipeline never activated (dead on EVERY
    //     finish). Re-wired with all 3 finishes. Prices re-queried live via
    //     Admin API. Stainless label = 'Stainless' to match swatch text.
    // Heart Cutout Necklace ("Molly") — live price $42.00
    47279575367829: { fulfillerVariant: 147729, templateProductId: '69e8c0ad7027eebee60950a0', label: 'Gold',      maxLength: 12, displayPrice: '$42.00' },
    47279575400597: { fulfillerVariant: 147731, templateProductId: '69e8c0ad7027eebee60950a0', label: 'Stainless', maxLength: 12, displayPrice: '$42.00' },
    47395385180309: { fulfillerVariant: 147730, templateProductId: '69e8c0ad7027eebee60950a0', label: 'Rose Gold', maxLength: 12, displayPrice: '$42.00' },
    // Paperclip Heart Necklace (paw + name) — live price $39.00
    47280265691285: { fulfillerVariant: 164438, templateProductId: '69e8fcc8a1496c73d004ea29', label: 'Gold',      maxLength: 10, displayPrice: '$42.00' },
    47280265724053: { fulfillerVariant: 164440, templateProductId: '69e8fcc8a1496c73d004ea29', label: 'Stainless', maxLength: 10, displayPrice: '$42.00' },
    47379752288405: { fulfillerVariant: 164439, templateProductId: '69e8fcc8a1496c73d004ea29', label: 'Rose Gold', maxLength: 10, displayPrice: '$42.00' },
    // Soul Dog Circle Necklace — live price $42.00
    47280667164821: { fulfillerVariant: 147652, templateProductId: '69e92e4a9269eec08009422d', label: 'Gold',      maxLength: 12, displayPrice: '$47.00' },
    47280667197589: { fulfillerVariant: 147655, templateProductId: '69e92e4a9269eec08009422d', label: 'Stainless', maxLength: 12, displayPrice: '$47.00' },
    47303766048917: { fulfillerVariant: 147654, templateProductId: '69e92e4a9269eec08009422d', label: 'Rose Gold', maxLength: 12, displayPrice: '$47.00' },
    // Initial Necklace — Engraved Heart Letter Pendant. 3 Shopify variants (one per finish).
    // Upstream product swapped 2026-06-15: old 6a2eb134f5f63e260010943d → new 6a302879a3f4990fcb08d373.
    // Pipeline updated 2026-06-24 to match Worker ALLOWED whitelist. maxLength=2 (char_limit in template).
    // Font choice rides in properties[Font] line-item property, not as a separate Shopify variant.
    // Variant IDs confirmed via Admin API 2026-06-24 (product 9243015544981).
    47445564620949: { fulfillerVariant: 147718, templateProductId: '6a302879a3f4990fcb08d373', label: 'Gold',      maxLength: 2, displayPrice: '$42.00' },  // Gold (18K Gold PVD)
    47445564719253: { fulfillerVariant: 147720, templateProductId: '6a302879a3f4990fcb08d373', label: 'Rose Gold', maxLength: 2, displayPrice: '$42.00' },  // Rose Gold (18K Rose Gold PVD)
    47445564817557: { fulfillerVariant: 147721, templateProductId: '6a302879a3f4990fcb08d373', label: 'Stainless', maxLength: 2, displayPrice: '$42.00' },  // Stainless (316L stainless steel)
    // Bella Forever Break-Apart Heart — 2 finishes (Gold, Stainless). Live price $59.00.
    // Shopify product pet-memorial-break-apart-heart-necklace; the fulfiller 69e8c92c7027eebee609546e
    // (whitelisted on Worker: variants 164443 Gold, 164444 Stainless). char_limit=10.
    // FIX 2026-07-05: was MISSING from VARIANT_MAP -> pipeline never activated -> ATC stuck disabled (live purchase blocker).
    47279766339733: { fulfillerVariant: 164443, templateProductId: '69e8c92c7027eebee609546e', label: 'Gold',      maxLength: 10, displayPrice: '$59.00' },
    47279766372501: { fulfillerVariant: 164444, templateProductId: '69e8c92c7027eebee609546e', label: 'Stainless', maxLength: 10, displayPrice: '$59.00' },
  };
  // Which upstream product this page is for — set at init() from the initial
  // variant. Used so a swatch-label lookup ("Silver") knows which of the
  // several Silver variants in VARIANT_MAP belongs to THIS page.
  let pageProductTemplateId = null;

  // ── MTL 3-font routing (Initials Necklace) ──────────────────────────────
  // Font choice is a line-item property (properties[Font]) — NOT a Shopify
  // variant. Each font maps to its own single-font upstream
  // template so the minted engraving art matches the on-screen preview.
  // Consulted ONLY on pages that render the font picker (hidden input
  // [data-mtl="font-input"] present); every other personalized SKU is
  // completely unaffected. Added 2026-07-07 (Option A).
  const FONT_TEMPLATE = {
    'Romantic':   '6a302879a3f4990fcb08d373', // Dancing Script (live template)
    'Timeless':   '6a4cfa5291eeefb5c20f405e', // Cormorant Garamond
  };
  function getCurrentFontLabel() {
    const el = document.querySelector('[data-mtl="font-input"]');
    return el ? (el.value || 'Romantic').trim() : null;
  }
  // Resolve which template to mint from: font override on font-picker pages,
  // otherwise the variant's own template (unchanged for all other SKUs).
  function resolveTemplateId(map) {
    const f = getCurrentFontLabel();
    if (f && FONT_TEMPLATE[f]) return FONT_TEMPLATE[f];
    return map.templateProductId;
  }
  let currentFontLabel = null;

  const TAG = '[MTL-Silent]';
  function log() { try { console.log.apply(console, [TAG, ...arguments]); } catch (_) {} }
  function warn() { try { console.warn.apply(console, [TAG, ...arguments]); } catch (_) {} }

  // State
  let debounceTimer = null;
  let latestTypedAt = 0;
  let latestSharedIdAt = 0;
  let currentSharedId = null;
  let lastProcessedText = null;
  let inFlight = false;
  let inFlightWatchdog = null;
  let currentAbortController = null;

  function setInFlight(val) {
    inFlight = !!val;
    clearTimeout(inFlightWatchdog);
    if (inFlight) {
      inFlightWatchdog = setTimeout(() => {
        if (inFlight) {
          warn('inFlight watchdog fired — force-clearing stuck state');
          try { currentAbortController && currentAbortController.abort(); } catch (_) {}
          inFlight = false;
          showError('Took too long — tap below to try again.');
          gateAtc('locked');
        }
      }, INFLIGHT_WATCHDOG_MS);
    }
  }

  function getForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  function getNameInput() {
    return document.querySelector('[data-mtl="name-input"]');
  }

  function getAtcBtn() {
    return document.querySelector('[data-mtl="atc-btn"]');
  }

  function getCurrentShopifyVariantId() {
    // Horizon's variant picker keeps the swatch's aria-pressed/active state
    // up to date the instant a swatch is clicked, but does NOT touch URL or
    // form input[name="id"] until cart submit. So we trust the active swatch
    // first, then URL, then form input. We scope the swatch-label lookup to
    // THIS page's product (pageProductTemplateId) so a "Silver" click on the
    // Heart page doesn't return the Paw Bar Silver variant by accident.
    try {
      const active = document.querySelector(
        'button.swatch.active, button.swatch[aria-pressed="true"], button.swatch[aria-checked="true"]'
      );
      // 2026-10-15: TIER A — the swatch carries the real Shopify variant id (rendered by Liquid from
      // product.variants), so it is language-proof (fr-CA labels are translated). Text match stays as fallback.
      if (active) {
        const dv = active.getAttribute('data-variant-id');
        if (dv && /^\d+$/.test(dv)) return parseInt(dv, 10);
      }
      if (active && pageProductTemplateId) {
        const txt = (active.textContent || '').toLowerCase();
        // Pick the LONGEST label that's a substring of the active swatch text.
        // Disambiguates "Gold" (4 chars) vs "Rose Gold" (9 chars) when both
        // appear in the swatch text "Rose Gold18K PVD". (Fix 2026-05-30.)
        let bestVid = null;
        let bestLen = 0;
        for (const vid in VARIANT_MAP) {
          const e = VARIANT_MAP[vid];
          if (e.templateProductId === pageProductTemplateId &&
              txt.indexOf(e.label.toLowerCase()) > -1 &&
              e.label.length > bestLen) {
            bestLen = e.label.length;
            bestVid = parseInt(vid, 10);
          }
        }
        if (bestVid !== null) return bestVid;
      }
    } catch (_) {}
    try {
      const m = window.location.search.match(/[?&]variant=(\d+)/);
      if (m) return parseInt(m[1], 10);
    } catch (_) {}
    const form = getForm();
    if (!form) return null;
    const idInput = form.querySelector('input[name="id"]');
    return idInput ? parseInt(idInput.value, 10) : null;
  }

  function getPreviewImg() {
    // Horizon theme + our custom section
    return document.querySelector(
      '.product__media img, .product-image img, [data-mtl="preview-image"], .product-gallery img, .product__primary-image'
    );
  }

  function setHidden(name, value) {
    const form = getForm();
    if (!form) return;
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function clearHidden(name) {
    const form = getForm();
    if (!form) return;
    form.querySelectorAll(`input[name="${name}"]`).forEach(el => el.remove());
  }

  function gateAtc(reason) {
    const atc = getAtcBtn();
    if (!atc) return;
    if (reason === 'locked') {
      atc.disabled = true;
      atc.setAttribute('aria-disabled', 'true');
      atc.dataset.mtlGate = 'locked';
    } else if (reason === 'loading') {
      // TIER 1 OPTIMISTIC UX (2026-04-25): keep button visually enabled with
      // the ready-state label while the Worker call runs in background. Form
      // submit handler holds clicks gracefully if pipeline still in flight.
      atc.disabled = false;
      atc.removeAttribute('aria-disabled');
      atc.dataset.mtlGate = 'loading';
      const text = normalizeText(getNameInput()?.value);
      const variantId = getCurrentShopifyVariantId();
      const price = (getForm() && getForm().dataset.price) || (VARIANT_MAP[variantId] && VARIANT_MAP[variantId].displayPrice) || '';
      setAtcLabel(atc, text, price);
    } else if (reason === 'ready') {
      atc.disabled = false;
      atc.removeAttribute('aria-disabled');
      atc.dataset.mtlGate = 'ready';
      // Set label including the name if present — show the normalized
      // (all-caps) value so the ATC label matches what gets engraved.
      const text = normalizeText(getNameInput()?.value);
      const variantId = getCurrentShopifyVariantId();
      const price = (getForm() && getForm().dataset.price) || (VARIANT_MAP[variantId] && VARIANT_MAP[variantId].displayPrice) || '';
      setAtcLabel(atc, text, price);
    }
  }

  // 2026-09-21: the ATC button is styled ALL CAPS. On exact-case SKUs
  // (data-uppercase="false") keep the customer's name in the case they typed
  // so the button never suggests a different engraving than what is sent.
  function setAtcLabel(atc, text, price) {
    const f = getForm();
    if (text && f && f.dataset && f.dataset.uppercase === 'false') {
      atc.textContent = 'Add to Cart — ';
      const s = document.createElement('span');
      s.className = 'mtl-atc-name';
      s.style.setProperty('text-transform', 'none', 'important');
      s.style.setProperty('pointer-events', 'none');  // clicks land on the button even if the label re-renders mid-click
      s.textContent = text;
      atc.appendChild(s);
      atc.appendChild(document.createTextNode(' · ' + price));
    } else {
      atc.textContent = text ? `Add to Cart — ${text} · ${price}` : `Add to Cart — ${price}`;
    }
  }

  function showError(msg) {
    let el = document.querySelector('[data-mtl="silent-error"]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-mtl', 'silent-error');
      el.setAttribute('role', 'alert');
      el.style.cssText = 'color:#b5362a;font-size:13px;margin:8px 0;padding:10px 14px;background:#fdf0ee;border-radius:8px;border:1px solid #f5c9c0;';
      const input = getNameInput();
      if (input?.parentElement) input.parentElement.appendChild(el);
    }
    el.textContent = msg;
    el.style.display = 'block';
  }

  function clearError() {
    const el = document.querySelector('[data-mtl="silent-error"]');
    if (el) el.style.display = 'none';
  }

  function revealPersonalizerButton() {
    // Failure fallback: show the hidden the fulfiller button so customer can still personalize
    document.querySelectorAll('.mtl-fulfillment-hidden, [data-mtl-fulfillment-hidden]').forEach(el => {
      el.style.setProperty('display', 'block', 'important');
      el.style.removeProperty('position');
      el.style.removeProperty('left');
      el.style.removeProperty('opacity');
    });
  }

  function swapPreview(previewUrl) {
    const img = getPreviewImg();
    if (!img || !previewUrl) return;
    if (!img.dataset.mtlOriginalSrc) img.dataset.mtlOriginalSrc = img.src;
    img.src = previewUrl;
    img.srcset = '';
  }

  // TIER 1 sessionStorage cache (2026-04-25): same (template, variant, text)
  // combo within one browser session reuses the sharedId — no Worker call.
  function cacheKey(templateId, variantId, text) {
    return 'mtl-pid:' + templateId + ':' + variantId + ':' + text;
  }
  function readCache(templateId, variantId, text) {
    try {
      const raw = sessionStorage.getItem(cacheKey(templateId, variantId, text));
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !obj.personalizationId) return null;
      return obj;
    } catch (_) { return null; }
  }
  function writeCache(templateId, variantId, text, payload) {
    try {
      sessionStorage.setItem(cacheKey(templateId, variantId, text), JSON.stringify({
        personalizationId: payload.personalizationId,
        previewUrl: payload.previewUrl || null,
        cachedAt: Date.now(),
      }));
    } catch (_) {}
  }

  async function fetchSharedId(text, shopifyVariantId) {
    const map = VARIANT_MAP[shopifyVariantId];
    if (!map) {
      warn('no variant map for', shopifyVariantId);
      return { fallback: true, reason: 'variant_not_mapped' };
    }
    const templateId = resolveTemplateId(map);
    // TIER 1: cache hit short-circuit
    const cached = readCache(templateId, map.fulfillerVariant, text);
    if (cached) {
      log('cache hit — reusing sharedId', cached.personalizationId);
      return { ok: true, personalizationId: cached.personalizationId, previewUrl: cached.previewUrl, _fromCache: true };
    }
    const body = {
      template_product_id: templateId,
      variant_id: map.fulfillerVariant,
      text,
    };
    currentAbortController = new AbortController();
    const ctrl = currentAbortController;
    const timeoutId = setTimeout(() => { try { ctrl.abort(); } catch (_) {} }, FETCH_TIMEOUT_MS);
    const startedAt = Date.now();
    try {
      const res = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json();
      log('worker responded in', Date.now() - startedAt, 'ms');
      if (!res.ok || data.fallback || data.error) {
        return { fallback: true, reason: data.reason || data.error || `http_${res.status}`, data };
      }
      return { ok: true, ...data };
    } catch (err) {
      clearTimeout(timeoutId);
      const reason = err && err.name === 'AbortError' ? 'timeout_or_aborted' : 'network_error';
      warn('worker fetch failed after', Date.now() - startedAt, 'ms —', reason, err);
      return { fallback: true, reason, err: String(err) };
    }
  }

  async function processText(text) {
    if (inFlight) return;
    if (!text || text.length < 1) {
      gateAtc('locked');
      clearHidden('properties[Personalization ID]');
          return;
    }
    // Invalidate any existing sharedId — customer changed text
    currentSharedId = null;
    clearHidden('properties[Personalization ID]');

    const shopifyVariantId = getCurrentShopifyVariantId();
    if (!shopifyVariantId) {
      warn('no variant id, cannot proceed');
      return;
    }

    setInFlight(true);
    gateAtc('loading');
    clearError();

    const result = await fetchSharedId(text, shopifyVariantId);
    setInFlight(false);

    // If text changed while we were fetching, ignore this result.
    // Compare normalized-to-normalized — input.value is the RAW (lowercase)
    // value the user typed; CSS text-transform:uppercase is cosmetic only.
    const currentText = normalizeText(getNameInput()?.value);
    if (currentText !== text) {
      log('stale response, ignoring (current=', currentText, 'sent=', text, ')');
      return;
    }

    if (result.fallback) {
      warn('fallback triggered:', result.reason);
      showError('Personalization temporarily unavailable — tap "Add personalized design" below.');
      revealPersonalizerButton();
      gateAtc('locked');
      return;
    }

    // Success — write both properties, swap preview, unlock ATC
    currentSharedId = result.personalizationId;
    latestSharedIdAt = Date.now();
    lastProcessedText = text;
    setHidden('properties[Personalization ID]', result.personalizationId);
      // Keep legacy readable props in sync for support
    setHidden('properties[_Engraving]', text);
    if (result.previewUrl) swapPreview(result.previewUrl);
    // TIER 1: write cache (skip if this result already came FROM cache)
    if (!result._fromCache) {
      const map = VARIANT_MAP[shopifyVariantId];
      if (map) writeCache(resolveTemplateId(map), map.fulfillerVariant, text, result);
    }
    gateAtc('ready');
    log('pipeline ready — sharedId:', result.personalizationId, result._fromCache ? '(cache)' : '(worker)');
  }

  function normalizeText(s) {
    // 2026-06-09: honor per-SKU case. Force ALL CAPS unless the section sets
    // data-uppercase="false" (proper-case engraving fonts — Pet Paw/Antonio,
    // Cross Bar/Inter, Heart Bar/Dancing Script). Keeps engraved text identical
    // to the live preview so what ships matches what the customer sees.
    s = (s || '').trim();
    var f = getForm();
    return (f && f.dataset && f.dataset.uppercase === 'false') ? s : s.toUpperCase();
  }

  function onInput() {
    const input = getNameInput();
    if (!input) return;
    const text = normalizeText(input.value);
    latestTypedAt = Date.now();
    clearTimeout(debounceTimer);
    gateAtc('loading');
    debounceTimer = setTimeout(() => {
      processText(text);
    }, DEBOUNCE_MS);
  }

  // Track current variant to detect changes
  let currentVariantId = null;

  function onVariantChange() {
    const newVariantId = getCurrentShopifyVariantId();
    if (newVariantId && newVariantId !== currentVariantId) {
      log('variant changed from', currentVariantId, 'to', newVariantId);
      currentVariantId = newVariantId;
      // Invalidate old sharedId — it was for a different variant
      currentSharedId = null;
      lastProcessedText = null;
      clearHidden('properties[Personalization ID]');
      // Abort any in-flight Worker call — its sharedId would be for old variant
      if (currentAbortController) {
        try { currentAbortController.abort(); } catch (_) {}
      }
      setInFlight(false);
      clearError();
      const input = getNameInput();
      const text = normalizeText(input?.value);
      if (text) {
        // Re-kick the pipeline for new variant — short delay (not full debounce)
        clearTimeout(debounceTimer);
        gateAtc('loading');
        debounceTimer = setTimeout(() => processText(text), 80);
      } else {
        gateAtc('locked');
      }
    }
  }

  // Re-mint when the buyer switches font (font-picker SKUs only). Mirrors
  // onVariantChange: same text, different template → fresh sharedId so the
  // engraving art matches the newly selected font's preview.
  function onFontChange() {
    const f = getCurrentFontLabel();
    if (f && f !== currentFontLabel) {
      log('font changed from', currentFontLabel, 'to', f);
      currentFontLabel = f;
      currentSharedId = null;
      lastProcessedText = null;
      clearHidden('properties[Personalization ID]');
      if (currentAbortController) { try { currentAbortController.abort(); } catch (_) {} }
      setInFlight(false);
      clearError();
      const input = getNameInput();
      const text = normalizeText(input && input.value);
      if (text) {
        clearTimeout(debounceTimer);
        gateAtc('loading');
        debounceTimer = setTimeout(() => processText(text), 80);
      } else {
        gateAtc('locked');
      }
    }
  }

  function init() {
    const input = getNameInput();
    const form = getForm();
    if (!input || !form) {
      log('required elements not found; skipping init');
      return;
    }
    input.addEventListener('input', onInput);
    input.addEventListener('change', onInput);

    // Read the initial variant from URL/form (before any swatch interaction)
    // so we can pin pageProductTemplateId. After this, getCurrentShopifyVariantId
    // will use swatch-aria detection scoped to this product.
    const initialVariantId = (function () {
      try {
        const m = window.location.search.match(/[?&]variant=(\d+)/);
        if (m) return parseInt(m[1], 10);
      } catch (_) {}
      const idInput = form.querySelector('input[name="id"]');
      return idInput ? parseInt(idInput.value, 10) : null;
    })();
    if (initialVariantId && VARIANT_MAP[initialVariantId]) {
      pageProductTemplateId = VARIANT_MAP[initialVariantId].templateProductId;
      // Apply per-product maxLength to the input element so the customer can't
      // type past the engraving limit (Heart=10, Paw Bar=15, etc.).
      const maxLen = VARIANT_MAP[initialVariantId].maxLength;
      if (maxLen) {
        input.setAttribute('maxlength', String(maxLen));
        // Also update the visible counter cap if the section renders one
        const counter = document.querySelector('[data-mtl="char-counter"]');
        if (counter) counter.dataset.max = String(maxLen);
      }
      log('init for product', VARIANT_MAP[initialVariantId].label,
          'template', pageProductTemplateId, 'maxLen', maxLen);
    } else {
      log('init: variant not in VARIANT_MAP — silent pipeline will not activate');
      return;
    }

    // Set initial variant
    currentVariantId = getCurrentShopifyVariantId();

    // Watch for variant changes — multiple mechanisms for Shopify theme variety
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      // MutationObserver on variant hidden input value attribute
      new MutationObserver(onVariantChange).observe(variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }
    // Also listen for any change events on the form (swatch clicks bubble up)
    form.addEventListener('change', onVariantChange);

    // Horizon-specific: swatches are <button class="swatch"> — button clicks
    // do NOT fire form `change` and their URL update comes asynchronously.
    // Capture them at document level, then re-check variant multiple times
    // to catch Horizon's async URL/input update.
    document.addEventListener('click', function (ev) {
      const swatch = ev.target && ev.target.closest &&
        ev.target.closest('button.swatch, .swatches button, variant-picker button, [data-variant-id], [data-option-value]');
      if (!swatch) return;
      log('swatch-like button clicked — probing variant');
      // probe at multiple intervals to catch whenever Horizon finishes the swap
      [0, 80, 200, 500, 900].forEach(ms => setTimeout(onVariantChange, ms));
    }, true);

    // Also listen for URL changes (pushState / replaceState / popstate)
    const _push = history.pushState;
    history.pushState = function () { _push.apply(this, arguments); setTimeout(onVariantChange, 0); };
    const _replace = history.replaceState;
    history.replaceState = function () { _replace.apply(this, arguments); setTimeout(onVariantChange, 0); };
    window.addEventListener('popstate', onVariantChange);

    // Font picker (multi-font SKUs only): initialise tracking + re-mint on switch.
    if (document.querySelector('[data-mtl="font-input"]')) {
      currentFontLabel = getCurrentFontLabel();
      log('font picker detected — initial font', currentFontLabel);
      document.addEventListener('click', function (ev) {
        const fbtn = ev.target && ev.target.closest && ev.target.closest('[data-mtl-font]');
        if (!fbtn) return;
        // personalization.js updates the hidden font-input on this same click;
        // probe a few ticks later so we read the new value.
        [0, 60, 160].forEach((ms) => setTimeout(onFontChange, ms));
      }, true);
    }

    // Poll every 500ms as last-resort failsafe
    setInterval(onVariantChange, 500);

    // Form submit guard — block ATC if no fresh sharedId OR variant drift
    form.addEventListener('submit', function (ev) {
      const text = normalizeText(input.value);
      if (!text) return;
      const latestVariant = getCurrentShopifyVariantId();

      // Horizon's swatch picker doesn't sync form input[name="id"] until its
      // own submit handler runs. We run first (capture phase) and force the
      // right variant so the sharedId and the cart line-item agree.
      if (latestVariant) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value !== String(latestVariant)) {
          log('forcing form variant id to', latestVariant, '(was', idInput.value, ')');
          idInput.value = String(latestVariant);
        }
      }

      const fresh = currentSharedId &&
                    lastProcessedText === text &&
                    latestSharedIdAt >= latestTypedAt &&
                    latestVariant === currentVariantId;
      if (fresh) return;  // Happy path

      // TIER 1 GRACEFUL WAIT (2026-04-25): hold submit briefly with inline
      // spinner instead of bouncing customer with an error.
      ev.preventDefault();
      ev.stopPropagation();
      currentVariantId = latestVariant;

      const atc = getAtcBtn();
      const originalLabel = atc ? atc.textContent : null;
      if (atc) {
        atc.textContent = 'Saving your design…';
        atc.disabled = true;
      }

      if (!inFlight) processText(text);

      const maxWaitMs = 8000;
      const startWait = Date.now();
      const poller = setInterval(function () {
        const newFresh = currentSharedId &&
                         lastProcessedText === text &&
                         latestSharedIdAt >= latestTypedAt &&
                         latestVariant === currentVariantId;
        if (newFresh) {
          clearInterval(poller);
          if (atc) atc.disabled = false;
          log('graceful wait succeeded — re-submitting');
          form.submit();
          return;
        }
        if (Date.now() - startWait > maxWaitMs) {
          clearInterval(poller);
          if (atc) {
            atc.disabled = false;
            if (originalLabel) atc.textContent = originalLabel;
          }
          showError('Personalization is taking longer than expected. Tap Add to Cart again to retry.');
        }
      }, 150);
    }, true);

    gateAtc('locked');
    log('silent pipeline initialized; variant=', currentVariantId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();



